import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatDate, todayKL } from "@/lib/format";
import { PageHeader } from "@/components/portal/PortalLayout";
import {
  Badge, Card, Empty, ErrorNote, Field, Loading, Note, ProgressBar, inputClass,
} from "@/components/portal/Ui";

const CLIENT_STATUSES = ["Upcoming", "Active", "On hold", "Completed"];
const PERIODS = [
  { value: "", label: "All time" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_quarter", label: "This quarter" },
  { value: "this_year", label: "This year" },
  { value: "custom", label: "Custom dates" },
];

function periodRange(period) {
  if (!period || period === "custom") return null;
  const now = new Date(`${todayKL()}T00:00:00`);
  const year = now.getFullYear();
  const month = now.getMonth();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (period === "this_month") return [iso(new Date(year, month, 1)), iso(new Date(year, month + 1, 0))];
  if (period === "last_month")
    return [iso(new Date(year, month - 1, 1)), iso(new Date(year, month, 0))];
  if (period === "this_quarter") {
    const start = Math.floor(month / 3) * 3;
    return [iso(new Date(year, start, 1)), iso(new Date(year, start + 3, 0))];
  }
  if (period === "this_year") return [`${year}-01-01`, `${year}-12-31`];
  return null;
}

/** A campaign matches a period if its dates overlap it at all. */
function overlaps(campaign, from, to) {
  const start = campaign.start_date || campaign.event_date || null;
  const end = campaign.end_date || campaign.event_date || null;
  if (from && end && end < from) return false;
  if (from && !end && start && start < from) return false;
  if (to && start && start > to) return false;
  if (to && !start && end && end > to) return false;
  return true;
}

export default function ClientCampaigns() {
  const [campaigns, setCampaigns] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") || "");
  const [statuses, setStatuses] = useState(
    (params.get("status") || "").split(",").filter((s) => CLIENT_STATUSES.includes(s))
  );
  const [period, setPeriod] = useState(params.get("period") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setCampaigns((prev) => prev || []);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  useEffect(() => {
    const next = {};
    if (search) next.q = search;
    if (statuses.length) next.status = statuses.join(",");
    if (period) next.period = period;
    if (period === "custom" && from) next.from = from;
    if (period === "custom" && to) next.to = to;
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statuses, period, from, to, setParams]);

  const visible = useMemo(() => {
    if (!campaigns) return [];
    const preset = periodRange(period);
    const rangeFrom = period === "custom" ? from : preset?.[0];
    const rangeTo = period === "custom" ? to : preset?.[1];
    const needle = search.trim().toLowerCase();
    return campaigns.filter((campaign) => {
      if (needle && !campaign.name.toLowerCase().includes(needle)) return false;
      if (statuses.length && !statuses.includes(campaign.status)) return false;
      if ((rangeFrom || rangeTo) && !overlaps(campaign, rangeFrom, rangeTo)) return false;
      return true;
    });
  }, [campaigns, search, statuses, period, from, to]);

  const toggleStatus = (status) =>
    setStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status]
    );

  if (campaigns === null) return <Loading label="Loading your campaigns…" />;

  return (
    <div data-testid="client-campaigns-page">
      <PageHeader
        title="My Campaigns"
        description="Progress and performance for every AnyTribe campaign run for your organisation."
        onRefresh={load}
        refreshing={refreshing}
      />
      <ErrorNote message={error} onRetry={load} />

      <div className="mb-5">
        <Note tone="neutral" testId="client-metrics-disclaimer">
          Performance metrics are manually submitted by creators or the AnyTribe team. Updates
          appear after they are saved.
        </Note>
      </div>

      {campaigns.length > 0 && (
        <Card className="mb-5" testId="client-filters">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr]">
            <Field label="Campaign name">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by campaign name…"
                className={inputClass}
                data-testid="client-search-input"
              />
            </Field>
            <Field label="Period">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className={inputClass}
                data-testid="client-period-select"
              >
                {PERIODS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {period === "custom" && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="From">
                <input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className={inputClass}
                  data-testid="client-from-input"
                />
              </Field>
              <Field label="To">
                <input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className={inputClass}
                  data-testid="client-to-input"
                />
              </Field>
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="client-status-filter">
            {CLIENT_STATUSES.map((status) => {
              const on = statuses.includes(status);
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => toggleStatus(status)}
                  aria-pressed={on}
                  data-testid={`client-status-toggle-${status.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                    on
                      ? "bg-[#111111] text-white"
                      : "border border-[#E6E1D8] bg-white text-[#6b6660] hover:border-[#111111]"
                  }`}
                >
                  {status}
                </button>
              );
            })}
            <span className="ml-auto text-xs body-text" data-testid="client-match-count">
              {visible.length} of {campaigns.length} campaign
              {campaigns.length === 1 ? "" : "s"}
            </span>
          </div>
        </Card>
      )}

      {campaigns.length === 0 ? (
        <Empty
          testId="client-no-campaigns"
          title="No campaigns yet"
          body="When AnyTribe sets up a campaign for your organisation it will appear here."
        />
      ) : visible.length === 0 ? (
        <Empty
          testId="client-no-matches"
          title="Nothing matches those filters"
          body="Try a different name, status or period — or clear the filters to see every campaign."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {visible.map((campaign) => {
            const expected = campaign.expected_posts_total || 0;
            const published = campaign.published_posts_total || 0;
            return (
              <Link
                key={campaign.id}
                to={`/client/campaigns/${campaign.id}`}
                data-testid={`client-campaign-card-${campaign.id}`}
                className="block transition-transform hover:-translate-y-0.5"
              >
                <Card className="h-full">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-extrabold">{campaign.name}</h2>
                      <p className="mt-1.5 text-sm body-text">
                        {formatDate(campaign.start_date)} – {formatDate(campaign.end_date)}
                      </p>
                    </div>
                    <Badge>{campaign.status}</Badge>
                  </div>

                  <div className="mt-5">
                    <div className="flex items-end justify-between">
                      <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#8a8378]">
                        Published posts
                      </p>
                      <p className="text-sm font-extrabold tabular" data-testid={`client-progress-${campaign.id}`}>
                        {published} / {expected || "—"}
                      </p>
                    </div>
                    <div className="mt-2">
                      <ProgressBar percent={expected ? (published / expected) * 100 : 0} />
                    </div>
                  </div>

                  <p className="mt-5 text-sm font-semibold text-[#F2811D]">
                    View campaign dashboard →
                  </p>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
